// Reporting duties, the seat's KPI scopes and the review of agent-reported numbers (AGENT-
// REPORTING; review build items #10 and #17; 13 §Reporting duties, 05 §Agent scopes, both
// founder-approved 2026-10-21). Every route is behind the `kpi` feature.
//
//   reporting.seat        `GET seats/{id}/reporting`: the seat's measured metrics with their duty,
//                         its status (Next due / Late / Blocked), the agent's generated order text,
//                         the last report and any value pending review, plus the seat's KPI
//                         scopes — Setup › KPI reporting in one call (seat viewers).
//   reporting.putDuty     `PUT seats/{id}/reporting-duties/{metric_def_id}`: set the duty
//   reporting.deleteDuty  `DELETE …`: remove it (a manager or the seat's supervisor)
//   seats.putKpiScopes    `PUT seats/{id}/kpi-scopes`: what the seat's DE runs may do with its
//                         KPIs (`seat.settings.kpi_scopes`; null = the default, both)
//
// The review queue is the approvals queue (`approvals.list?kind=kpi_value`); its decisions are
// `approvals.approve | edit | reject`, which record the value in the deciding transaction.
import { z } from "zod";
import {
  MetricCadence,
  MetricSource,
  ReportingMethod,
  ReportingRemind,
  TrackingMode,
} from "../enums";
import { PeriodKey, Timestamp, Uuid } from "../primitives";
import { DueRule, PendingReview, ReportingDuty, ReportingStatus } from "../reporting";
import { LastReport, ObservationProvenance } from "../scorecard";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const kpi = { feature: "kpi" } as const;

export const SeatDutyParams = z.object({ co: Uuid, id: Uuid, metric_def_id: Uuid });

export const ReportingDutyPut = z
  .strictObject({
    method: ReportingMethod,
    due_rule: DueRule,
    lead_minutes: z.int().min(0).max(1440).optional(),
    evidence_required: z.boolean().optional(),
    remind: z
      .array(ReportingRemind)
      .max(2)
      .refine((r) => new Set(r).size === r.length, "no repeats")
      .optional(),
  })
  .meta({ id: "ReportingDutyPut" });
export type ReportingDutyPut = z.infer<typeof ReportingDutyPut>;

/** The two KPI scopes a seat's DE runs may hold (05 §Agent scopes). */
export const SeatKpiScope = z.enum(["kpis:read", "kpis:write"]);

export const SeatKpiScopesPut = z
  .strictObject({
    /** null: the default (both); `[]`: the KPI tools are off for the seat's runs. */
    kpi_scopes: z.array(SeatKpiScope).max(2).nullable(),
  })
  .meta({ id: "SeatKpiScopesPut" });

export const SeatKpiScopes = z
  .object({
    seat_id: Uuid,
    /** What the seat's settings name (null: nothing, the default applies). */
    kpi_scopes: z.array(SeatKpiScope).nullable(),
    /** What a DE run on the seat gets. */
    effective: z.array(SeatKpiScope),
  })
  .meta({ id: "SeatKpiScopes" });

export const SeatReportingMetric = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    source: MetricSource,
    cadence: MetricCadence.nullable(),
    tracking_mode: TrackingMode,
    needs_review: z.boolean(),
    /** How a DE reports it (`kpis.read` `report`). */
    report: z.enum(["value", "event", "none"]),
    duty: ReportingDuty.nullable(),
    status: ReportingStatus.nullable(),
    /** The DE's generated standing order for the duty (a DE seat with a duty only). */
    order_text: z.string().nullable(),
    last_report: LastReport.nullable(),
    pending_review: PendingReview.nullable(),
  })
  .meta({ id: "SeatReportingMetric" });

export const SeatReporting = z
  .object({
    seat: z.object({ id: Uuid, title: z.string(), supervisor_seat_id: Uuid.nullable() }),
    /** The seat's current holder: a person, a digital employee, or nobody (open). */
    holder: z
      .object({
        membership_id: Uuid,
        kind: z.enum(["person", "digital_employee"]),
        name: z.string(),
      })
      .nullable(),
    timezone: z.string(),
    kpi_scopes: SeatKpiScopes,
    /** The caller may change duties, scopes and review flags here. */
    can_edit: z.boolean(),
    metrics: z.array(SeatReportingMetric),
    as_of: Timestamp,
  })
  .meta({ id: "SeatReporting" });
export type SeatReporting = z.infer<typeof SeatReporting>;

/**
 * `approval.proposal.change` of a `kpi_value` approval: what the agent reported and where. An
 * edit may change `value` (kind value) or `quantity` (kind event) only.
 */
export const KpiValueChange = z
  .strictObject({
    metric_def_id: Uuid,
    metric_key: z.string(),
    metric_name: z.string(),
    seat_id: Uuid,
    seat_title: z.string(),
    kind: z.enum(["value", "event"]),
    value: z.number().nullable(),
    quantity: z.number().nullable(),
    unit: z.string(),
    period_type: z.string(),
    period_key: PeriodKey,
    occurred_at: Timestamp.nullable(),
    event_type: z.string().nullable(),
    provenance: ObservationProvenance,
  })
  .meta({ id: "KpiValueChange" });
export type KpiValueChange = z.infer<typeof KpiValueChange>;

export const reportingRoutes = {
  "reporting.seat": defineRoute({
    ...kpi,
    id: "reporting.seat",
    method: "get",
    path: `${base}/seats/{id}/reporting`,
    tag: "reporting",
    summary:
      "The seat's reporting: duties with Next due / Late / Blocked, review flags, KPI scopes",
    access: "seat_viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatReporting },
    errors: [],
    emits: [],
  }),
  "reporting.putDuty": defineRoute({
    ...kpi,
    id: "reporting.putDuty",
    method: "put",
    path: `${base}/seats/{id}/reporting-duties/{metric_def_id}`,
    tag: "reporting",
    summary: "Set the reporting duty of a seat measurable (a manager or the seat's supervisor)",
    access: "member",
    params: SeatDutyParams,
    body: ReportingDutyPut,
    response: { status: 200, schema: ReportingDuty },
    errors: ["seat_closed"],
    emits: ["reporting_duty.changed"],
  }),
  "reporting.deleteDuty": defineRoute({
    ...kpi,
    id: "reporting.deleteDuty",
    method: "delete",
    path: `${base}/seats/{id}/reporting-duties/{metric_def_id}`,
    tag: "reporting",
    summary: "Remove the reporting duty of a seat measurable",
    access: "member",
    params: SeatDutyParams,
    response: { status: 204 },
    errors: [],
    emits: ["reporting_duty.changed"],
  }),
  "seats.putKpiScopes": defineRoute({
    ...kpi,
    id: "seats.putKpiScopes",
    method: "put",
    path: `${base}/seats/{id}/kpi-scopes`,
    tag: "reporting",
    summary: "What the seat's digital employee runs may do with its KPIs (read, record)",
    access: "member",
    params: CompanyItemParams,
    body: SeatKpiScopesPut,
    response: { status: 200, schema: SeatKpiScopes },
    errors: ["seat_closed"],
    emits: ["seat.updated"],
  }),
} as const;
